import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { TransferOfCareReceivingProviderQuestionsLinkIds } from "@/encounters/constants/transfer-of-care";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Questionnaires } from "@/fhir/enums/questionnaires";

const ReceivingProviderDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.TransferOfCareReceivingProvider,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return null;

  const previousReceivingProviderAnswers = getQuestionnaireAnswers(previousVersion);
  const currentReceivingProviderAnswers = getQuestionnaireAnswers(currentVersion);

  const previousReceivingProviderName =
    previousReceivingProviderAnswers[TransferOfCareReceivingProviderQuestionsLinkIds.PracticeName]
      ?.valueString;
  const currentReceivingProviderName =
    currentReceivingProviderAnswers[TransferOfCareReceivingProviderQuestionsLinkIds.PracticeName]
      ?.valueString;

  const previousReceivingProviderNameAndCredentials =
    previousReceivingProviderAnswers[
      TransferOfCareReceivingProviderQuestionsLinkIds.NameAndCredentials
    ]?.valueString;
  const currentReceivingProviderNameAndCredentials =
    currentReceivingProviderAnswers[
      TransferOfCareReceivingProviderQuestionsLinkIds.NameAndCredentials
    ]?.valueString;

  const previousReceivingProviderAddress =
    previousReceivingProviderAnswers[TransferOfCareReceivingProviderQuestionsLinkIds.Address]
      ?.valueString;
  const currentReceivingProviderAddress =
    currentReceivingProviderAnswers[TransferOfCareReceivingProviderQuestionsLinkIds.Address]
      ?.valueString;

  const previousReceivingProviderTelephoneNumber =
    previousReceivingProviderAnswers[
      TransferOfCareReceivingProviderQuestionsLinkIds.TelephoneNumber
    ]?.valueString;
  const currentReceivingProviderTelephoneNumber =
    currentReceivingProviderAnswers[TransferOfCareReceivingProviderQuestionsLinkIds.TelephoneNumber]
      ?.valueString;

  const previousReceivingProviderFaxNumber =
    previousReceivingProviderAnswers[TransferOfCareReceivingProviderQuestionsLinkIds.FaxNumber]
      ?.valueString;
  const currentReceivingProviderFaxNumber =
    currentReceivingProviderAnswers[TransferOfCareReceivingProviderQuestionsLinkIds.FaxNumber]
      ?.valueString;

  const previousReceivingProviderEmailAddress =
    previousReceivingProviderAnswers[TransferOfCareReceivingProviderQuestionsLinkIds.EmailAddress]
      ?.valueString;
  const currentReceivingProviderEmailAddress =
    currentReceivingProviderAnswers[TransferOfCareReceivingProviderQuestionsLinkIds.EmailAddress]
      ?.valueString;

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Receiving Provider</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Practice Name</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentReceivingProviderName}
                  previous={previousReceivingProviderName}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Name and Credentials</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentReceivingProviderNameAndCredentials}
                  previous={previousReceivingProviderNameAndCredentials}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Address</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentReceivingProviderAddress}
                  previous={previousReceivingProviderAddress}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Telephone Number</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentReceivingProviderTelephoneNumber}
                  previous={previousReceivingProviderTelephoneNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Fax Number</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentReceivingProviderFaxNumber}
                  previous={previousReceivingProviderFaxNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Email</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentReceivingProviderEmailAddress}
                  previous={previousReceivingProviderEmailAddress}
                />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Practice Name</TextWrapper>
                <WordsDiff
                  current={currentReceivingProviderName}
                  previous={previousReceivingProviderName}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Name and Credentials</TextWrapper>
                <WordsDiff
                  current={currentReceivingProviderNameAndCredentials}
                  previous={previousReceivingProviderNameAndCredentials}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Address</TextWrapper>
                <WordsDiff
                  current={currentReceivingProviderAddress}
                  previous={previousReceivingProviderAddress}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Telephone Number</TextWrapper>
                <WordsDiff
                  current={currentReceivingProviderTelephoneNumber}
                  previous={previousReceivingProviderTelephoneNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Fax Number</TextWrapper>
                <WordsDiff
                  current={currentReceivingProviderFaxNumber}
                  previous={previousReceivingProviderFaxNumber}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Email Address</TextWrapper>
                <WordsDiff
                  current={currentReceivingProviderEmailAddress}
                  previous={previousReceivingProviderEmailAddress}
                />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default ReceivingProviderDiff;
